import { useState } from 'react';
import {
  LayoutDashboard,
  Layers,
  Users,
  CreditCard,
  Settings,
  Activity,
  User,
  LogOut,
  Building2,
  ChevronDown,
  Menu,
  X,
  Plus,
  ShieldAlert,
  UserCog,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';

interface AppLayoutProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  children: React.ReactNode;
}

const navItems = [
  { path: '/', label: 'Overview', icon: LayoutDashboard },
  { path: '/products', label: 'Products & Services', icon: Layers },
  { path: '/members', label: 'Members', icon: Users },
  { path: '/billing', label: 'Billing & Invoices', icon: CreditCard },
  { path: '/settings', label: 'Organisation Settings', icon: Settings },
  { path: '/activity', label: 'Audit Activity', icon: Activity },
  { path: '/account', label: 'Personal Account', icon: User },
];

export function AppLayout({ currentPath, onNavigate, children }: AppLayoutProps) {
  const { session, activeOrg, activeRole, switchOrg, logout, switchAccount } = useAuth();
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const memberships = session?.memberships || [];
  const principal = session?.principal;
  const isPlatformAdmin = Boolean(principal?.is_platform_admin);

  const isManager = activeRole === 'owner' || activeRole === 'admin';
  const isBillingRole = activeRole === 'owner' || activeRole === 'admin' || activeRole === 'billing';

  const visibleNavItems = navItems.filter((item) => {
    if (item.path === '/members' || item.path === '/settings' || item.path === '/activity') {
      return isManager;
    }
    if (item.path === '/billing') {
      return isBillingRole;
    }
    return true;
  });

  const handleOrgSwitch = async (orgId: string) => {
    setOrgDropdownOpen(false);
    await switchOrg(orgId);
    onNavigate('/');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-canvas)' }}>
      {/* Desktop Sidebar */}
      <aside
        style={{
          width: 260,
          background: 'var(--bg-surface)',
          borderRight: '1px solid var(--border-subtle)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          height: '100vh',
        }}
        className="hidden-mobile"
      >
        <div style={{ padding: '20px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Logo variant="lockup" size={32} tagline="NEXUS" />
            <Badge tone="accent">v0.1</Badge>
          </div>
        </div>

        {/* Organisation Switcher Button */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', position: 'relative' }}>
          <button
            onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 10px',
              background: 'var(--bg-surface-raised)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              textAlign: 'left',
              transition: 'background var(--duration-fast)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-surface-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--bg-surface-raised)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
              <Building2 size={16} color="var(--accent)" style={{ flexShrink: 0 }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Organisation
                </div>
                <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--fw-semibold)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {activeOrg?.display_name || 'No organisation'}
                </div>
              </div>
            </div>
            <ChevronDown size={14} color="var(--text-tertiary)" style={{ flexShrink: 0, transform: orgDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>

          {/* Org Dropdown */}
          {orgDropdownOpen && (
            <>
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                onClick={() => setOrgDropdownOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% - 4px)',
                  left: 16,
                  right: 16,
                  background: 'var(--bg-surface-raised)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-lg)',
                  zIndex: 50,
                  overflow: 'hidden',
                  padding: 4,
                }}
              >
                <div style={{ padding: '6px 8px', fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: 600 }}>
                  Your Organisations
                </div>
                {memberships.map((m) => {
                  const org = m.organisation;
                  if (!org) return null;
                  const isCurrent = org.id === activeOrg?.id;
                  return (
                    <button
                      key={org.id}
                      onClick={() => handleOrgSwitch(org.id)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 10px',
                        background: isCurrent ? 'var(--accent-soft)' : 'transparent',
                        borderRadius: 'var(--radius-sm)',
                        color: isCurrent ? 'var(--accent)' : 'var(--text-primary)',
                        textAlign: 'left',
                        fontSize: 'var(--text-sm)',
                      }}
                      onMouseEnter={(e) => {
                        if (!isCurrent) e.currentTarget.style.background = 'var(--bg-surface-hover)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isCurrent) e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1, paddingRight: 8 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {org.display_name}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                          {m.role} • {org.country}
                        </div>
                      </div>
                      {isCurrent && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)' }} />}
                    </button>
                  );
                })}
                <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: 4, paddingTop: 4 }}>
                  <button
                    onClick={() => {
                      setOrgDropdownOpen(false);
                      onNavigate('/onboarding');
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      color: 'var(--accent)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--fw-medium)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--accent-soft)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <Plus size={14} /> Create new organisation
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Navigation list */}
        <nav style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const active = currentPath === item.path;
            return (
              <button
                key={item.path}
                onClick={() => onNavigate(item.path)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '9px 12px',
                  borderRadius: 'var(--radius-md)',
                  color: active ? 'var(--accent)' : 'var(--text-secondary)',
                  background: active ? 'var(--accent-soft)' : 'transparent',
                  fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                  fontSize: 'var(--text-sm)',
                  textAlign: 'left',
                  transition: 'all var(--duration-fast)',
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.color = 'var(--text-primary)';
                    e.currentTarget.style.background = 'var(--bg-surface-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.color = 'var(--text-secondary)';
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <Icon size={18} color={active ? 'var(--accent)' : 'currentColor'} />
                <span>{item.label}</span>
              </button>
            );
          })}

          {isPlatformAdmin && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
              <div style={{ padding: '0 12px 6px', fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                CYRN Platform
              </div>
              <button
                onClick={() => onNavigate('/admin')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 'var(--radius-md)',
                  color: currentPath.startsWith('/admin') ? 'var(--danger)' : 'var(--text-secondary)',
                  background: currentPath.startsWith('/admin') ? 'rgba(239,68,68,0.10)' : 'transparent',
                  fontWeight: currentPath.startsWith('/admin') ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                  fontSize: 'var(--text-sm)',
                  textAlign: 'left',
                  transition: 'all var(--duration-fast)',
                }}
                onMouseEnter={(e) => {
                  if (!currentPath.startsWith('/admin')) {
                    e.currentTarget.style.color = 'var(--text-primary)';
                    e.currentTarget.style.background = 'var(--bg-surface-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!currentPath.startsWith('/admin')) {
                    e.currentTarget.style.color = 'var(--text-secondary)';
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <ShieldAlert size={18} color={currentPath.startsWith('/admin') ? 'var(--danger)' : 'currentColor'} />
                <span>Admin Console</span>
              </button>
            </div>
          )}
        </nav>

        {/* Connected account footer */}
        <div style={{ padding: '12px 16px 16px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-raised)', position: 'relative' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 6 }}>
            Signed in as
          </div>

          <button
            onClick={() => setAccountMenuOpen(!accountMenuOpen)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '8px 10px',
              background: 'var(--bg-inset)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-surface-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--bg-inset)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--navy-700)',
                  border: '1px solid var(--border-default)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--accent)',
                  fontWeight: 600,
                  fontSize: 'var(--text-xs)',
                  flexShrink: 0,
                }}
              >
                {principal?.display_name?.charAt(0).toUpperCase() || principal?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {principal?.display_name || 'Authenticated User'}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {principal?.email || (activeRole ? `Role: ${activeRole}` : '')}
                </div>
              </div>
            </div>
            <ChevronDown
              size={14}
              color="var(--text-tertiary)"
              style={{ flexShrink: 0, transform: accountMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
            />
          </button>

          {accountMenuOpen && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setAccountMenuOpen(false)} />
              <div
                style={{
                  position: 'absolute',
                  bottom: 'calc(100% - 8px)',
                  left: 16,
                  right: 16,
                  background: 'var(--bg-surface-raised)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-lg)',
                  zIndex: 50,
                  padding: 4,
                }}
              >
                <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)', marginBottom: 4 }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>CYRN Auth identity</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {principal?.subject}
                  </div>
                </div>

                <button
                  onClick={() => {
                    setAccountMenuOpen(false);
                    onNavigate('/account');
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    color: 'var(--text-primary)',
                    fontSize: 'var(--text-xs)',
                    borderRadius: 'var(--radius-sm)',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <User size={14} /> Account details
                </button>

                <button
                  onClick={switchAccount}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    color: 'var(--accent)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--fw-medium)',
                    borderRadius: 'var(--radius-sm)',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--accent-soft)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <UserCog size={14} /> Switch account
                </button>

                <button
                  onClick={logout}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    color: 'var(--danger)',
                    fontSize: 'var(--text-xs)',
                    borderRadius: 'var(--radius-sm)',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--danger-tint)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <LogOut size={14} /> Sign out everywhere
                </button>
              </div>
            </>
          )}
        </div>
      </aside>

      {/* Main content wrapper */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflowX: 'hidden' }}>
        {/* Mobile top navigation header */}
        <header
          className="visible-mobile"
          style={{
            display: 'none',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: 'var(--bg-surface)',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <Logo variant="lockup" size={28} />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              padding: 8,
              color: 'var(--text-primary)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-surface-raised)',
              border: '1px solid var(--border-subtle)',
              display: 'inline-flex',
            }}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </header>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div
            className="visible-mobile"
            style={{
              background: 'var(--bg-surface)',
              borderBottom: '1px solid var(--border-default)',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>Active Organisation</div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{activeOrg?.display_name || 'None'}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <Button size="sm" variant="secondary" onClick={() => { onNavigate('/org-picker'); setMobileMenuOpen(false); }}>
                  Switch Org
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { onNavigate('/onboarding'); setMobileMenuOpen(false); }}>
                  + New Org
                </Button>
              </div>
            </div>
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const active = currentPath === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    onNavigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    color: active ? 'var(--accent)' : 'var(--text-secondary)',
                    background: active ? 'var(--accent-soft)' : 'transparent',
                    fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                    fontSize: 'var(--text-sm)',
                    textAlign: 'left',
                  }}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </button>
              );
            })}
            {isPlatformAdmin && (
              <button
                onClick={() => {
                  onNavigate('/admin');
                  setMobileMenuOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  color: currentPath.startsWith('/admin') ? 'var(--danger)' : 'var(--text-secondary)',
                  background: currentPath.startsWith('/admin') ? 'rgba(239,68,68,0.10)' : 'transparent',
                  fontWeight: 'var(--fw-medium)',
                  fontSize: 'var(--text-sm)',
                  textAlign: 'left',
                }}
              >
                <ShieldAlert size={18} />
                <span>Admin Console</span>
              </button>
            )}
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, marginTop: 8 }}>
              <div style={{ padding: '0 12px 8px' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                  Signed in as
                </div>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)', fontWeight: 600 }}>
                  {principal?.display_name || 'Authenticated User'}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{principal?.email}</div>
              </div>
              <button
                onClick={switchAccount}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: 'var(--accent)',
                  fontSize: 'var(--text-sm)',
                  padding: '8px 12px',
                  width: '100%',
                }}
              >
                <UserCog size={16} /> Switch account
              </button>
              <button
                onClick={logout}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: 'var(--danger)',
                  fontSize: 'var(--text-sm)',
                  padding: '8px 12px',
                  width: '100%',
                }}
              >
                <LogOut size={16} /> Sign out
              </button>
            </div>
          </div>
        )}

        {/* Page Content Viewport */}
        <main style={{ flex: 1, padding: 'var(--space-8) var(--space-8)', maxWidth: 'var(--container-max)', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
          {children}
        </main>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .hidden-mobile {
            display: none !important;
          }
          .visible-mobile {
            display: flex !important;
          }
        }
      `}</style>
    </div>
  );
}
